import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import {
  Shield,
  Eye,
  AlertTriangle,
  Users,
  Plus,
  Phone,
  MapPin,
  X,
  RefreshCw,
  Info
} from 'lucide-react';

// Custom pin icons
const createPinIcon = (color) => {
  return L.divIcon({
    className: 'custom-pin',
    html: `<div style="background: ${color}; border: 3px solid white; border-radius: 50%; width: 24px; height: 24px; box-shadow: 0 2px 8px ${color}80;"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const pinIcons = {
  blue: createPinIcon('#3B82F6'),
  red: createPinIcon('#EF4444'),
  yellow: createPinIcon('#F59E0B')
};

const pinTypes = [
  {
    type: 'blue',
    label: 'Eyes on the Street',
    description: 'Report suspicious activity or vehicles',
    icon: Eye,
    color: 'bg-blue-500'
  },
  {
    type: 'red',
    label: 'Urgent Concern',
    description: 'Report fire, medical issue, or crime in progress',
    icon: AlertTriangle,
    color: 'bg-red-500'
  },
  {
    type: 'yellow',
    label: 'Community Watch',
    description: 'General safety tips or welfare checks',
    icon: Users,
    color: 'bg-amber-500'
  }
];

// Map click handler component
const MapClickHandler = ({ onMapClick, isAddingPin }) => {
  useMapEvents({
    click: (e) => {
      if (isAddingPin) {
        onMapClick(e.latlng);
      }
    }
  });
  return null;
};

// Center on location component
const CenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, 15);
    }
  }, [map, position]);
  return null;
};

const SafetyMapPage = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [selectedPinType, setSelectedPinType] = useState(null);
  const [clickedLocation, setClickedLocation] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [showDialog, setShowDialog] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Default center: Nassau, Bahamas (Killarney area)
  const defaultCenter = [25.0443, -77.3504];

  const fetchPins = useCallback(async () => {
    try {
      const params = filterType !== 'all' ? { pin_type: filterType } : {};
      const response = await axios.get(`${API}/safety-pins`, { params });
      setPins(response.data);
    } catch (error) {
      console.error('Failed to fetch pins:', error);
      toast.error('Failed to load safety reports');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          console.log('Location access denied');
        }
      );
    }
  }, []);

  const handleMapClick = (latlng) => {
    if (selectedPinType) {
      setClickedLocation(latlng);
      setShowDialog(true);
    }
  };

  const handleSubmitPin = async () => {
    if (!clickedLocation || !selectedPinType || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/safety-pins`, {
        pin_type: selectedPinType,
        title: formData.title,
        description: formData.description,
        latitude: clickedLocation.lat,
        longitude: clickedLocation.lng,
        anonymous: true
      });

      toast.success('Safety report submitted successfully!');
      setShowDialog(false);
      setFormData({ title: '', description: '' });
      setClickedLocation(null);
      setIsAddingPin(false);
      setSelectedPinType(null);
      fetchPins();
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const cancelAddPin = () => {
    setIsAddingPin(false);
    setSelectedPinType(null);
    setClickedLocation(null);
    setShowDialog(false);
    setFormData({ title: '', description: '' });
  };

  const filteredPins = pins;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Emergency Banner */}
      <Alert className="rounded-none border-x-0 border-t-0 bg-red-50 border-red-200">
        <Phone className="h-5 w-5 text-red-600" />
        <AlertTitle className="text-red-800 font-heading font-bold">Emergency? Call 911</AlertTitle>
        <AlertDescription className="text-red-700">
          This platform is NOT a substitute for emergency services. If you are in immediate danger, dial 911 immediately.
        </AlertDescription>
      </Alert>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-10 h-10 text-plp-blue" />
            Safer Together
          </h1>
          <p className="mt-2 text-slate-600">
            Report suspicious activity, urgent concerns, or community watch items. All reports are anonymous by default.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar Controls */}
          <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
            {/* Add Pin Section */}
            <Card className="bg-white" data-testid="add-pin-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">Report a Concern</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!isAddingPin ? (
                  <Button
                    onClick={() => setIsAddingPin(true)}
                    className="w-full bg-plp-blue hover:bg-plp-blue-light text-white"
                    data-testid="start-add-pin-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Report
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">Select report type:</p>
                    {pinTypes.map((pin) => (
                      <button
                        key={pin.type}
                        onClick={() => setSelectedPinType(pin.type)}
                        data-testid={`pin-type-${pin.type}`}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                          selectedPinType === pin.type
                            ? 'border-plp-blue bg-plp-blue/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${pin.color} rounded-full flex items-center justify-center`}>
                            <pin.icon className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{pin.label}</p>
                            <p className="text-xs text-slate-500">{pin.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                    {selectedPinType && (
                      <p className="text-sm text-plp-blue font-medium flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Click on the map to place your pin
                      </p>
                    )}
                    <Button
                      variant="outline"
                      onClick={cancelAddPin}
                      className="w-full"
                      data-testid="cancel-add-pin-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filter Section */}
            <Card className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">Filter Reports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`w-full p-2 rounded-lg text-left text-sm ${filterType === 'all' ? 'bg-plp-blue/10 text-plp-blue' : 'hover:bg-slate-50'}`}
                  data-testid="filter-all"
                >
                  All Reports ({pins.length})
                </button>
                {pinTypes.map((pin) => (
                  <button
                    key={pin.type}
                    onClick={() => setFilterType(pin.type)}
                    data-testid={`filter-${pin.type}`}
                    className={`w-full p-2 rounded-lg text-left text-sm flex items-center gap-2 ${filterType === pin.type ? 'bg-plp-blue/10 text-plp-blue' : 'hover:bg-slate-50'}`}
                  >
                    <div className={`w-3 h-3 rounded-full ${pin.color}`}></div>
                    {pin.label}
                  </button>
                ))}
                <Button variant="ghost" size="sm" onClick={fetchPins} className="w-full mt-2" data-testid="refresh-pins">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Legend
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {pinTypes.map((pin) => (
                  <div key={pin.type} className="flex items-center gap-2 text-sm">
                    <div className={`w-4 h-4 rounded-full ${pin.color}`}></div>
                    <span>{pin.label}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <Card className="bg-white overflow-hidden" data-testid="safety-map-container">
              <CardContent className="p-0">
                <div className="h-[400px] sm:h-[500px] lg:h-[600px] relative">
                  {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                      <RefreshCw className="w-8 h-8 text-plp-blue animate-spin" />
                    </div>
                  ) : (
                    <MapContainer
                      center={userLocation || defaultCenter}
                      zoom={14}
                      className="h-full w-full"
                      style={{ zIndex: 1 }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClickHandler onMapClick={handleMapClick} isAddingPin={isAddingPin && selectedPinType} />
                      {userLocation && <CenterMap position={userLocation} />}
                      
                      {/* Existing pins */}
                      {filteredPins.map((pin) => (
                        <Marker
                          key={pin.id}
                          position={[pin.latitude, pin.longitude]}
                          icon={pinIcons[pin.pin_type]}
                        >
                          <Popup>
                            <div className="p-2 min-w-[200px]">
                              <Badge className={`mb-2 ${
                                pin.pin_type === 'blue' ? 'bg-blue-500' :
                                pin.pin_type === 'red' ? 'bg-red-500' : 'bg-amber-500'
                              }`}>
                                {pinTypes.find(p => p.type === pin.pin_type)?.label}
                              </Badge>
                              <h3 className="font-heading font-semibold">{pin.title}</h3>
                              <p className="text-sm text-slate-600 mt-1">{pin.description}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                {new Date(pin.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                      ))}

                      {/* Clicked location marker */}
                      {clickedLocation && (
                        <Marker
                          position={[clickedLocation.lat, clickedLocation.lng]}
                          icon={pinIcons[selectedPinType] || pinIcons.blue}
                        />
                      )}
                    </MapContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Pin Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedPinType && (
                <span className="flex items-center gap-2">
                  {(() => {
                    const pin = pinTypes.find(p => p.type === selectedPinType);
                    if (pin) {
                      const Icon = pin.icon;
                      return (
                        <>
                          <div className={`w-8 h-8 ${pin.color} rounded-full flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          {pin.label}
                        </>
                      );
                    }
                    return null;
                  })()}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPinType === 'red' && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700 text-sm">
                <strong>If this is an emergency, dial 911 immediately!</strong> This report will be monitored by the campaign team.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Brief description..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="pin-title-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Details</Label>
              <Textarea
                id="description"
                placeholder="Provide more details..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="pin-description-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Your report will be submitted anonymously.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSubmitPin}
                className="flex-1 bg-plp-blue hover:bg-plp-blue-light"
                data-testid="submit-pin-btn"
              >
                Submit Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyMapPage;
